// Pure helpers of a Vault's Configuration and Logs tabs: what the settings form shows, what a save sends, the restart
// progress and the log lines. node --test src/lib/vaultconfig.test.ts
import type { ConfigSetting, ConfigSummary, VaultConfigData, SettingValue } from './api.ts';

export type Values = Record<string, SettingValue>;
export type Source = 'default' | 'vault.json' | 'cloud';

export const SOURCE_LABEL: Record<Source, string> = { default: 'default', 'vault.json': 'vault.json', cloud: 'Cloud override' };

/** Groups in display order: General first, the rest as the Vault listed them. */
export function settingGroups(schema: ConfigSetting[]): { name: string; settings: ConfigSetting[] }[] {
  const groups = new Map<string, ConfigSetting[]>();
  for (const s of schema) {
    const g = s.group?.trim() || 'General';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(s);
  }
  const names = [...groups.keys()].sort((a, b) => (a === 'General' ? -1 : 0) - (b === 'General' ? -1 : 0));
  return names.map((name) => ({ name, settings: groups.get(name)! }));
}

/**
 * True while the Vault has not answered the newest desired set: it is newer than the report and either nobody has
 * answered since it was saved, or the answer was not "applied" (a rejected or rolled-back set stays stored in the
 * Cloud, but the Vault does not run it, so nothing is "on its way").
 */
export function undelivered(data: Pick<VaultConfigData, 'report' | 'desired' | 'reportedAt'>): boolean {
  const r = data.report;
  const d = data.desired;
  if (!r || !d || d.version <= r.version) return false;
  return !answered(data);
}

/** The Vault reported (a refusal, a rollback) after the newest desired set was saved. */
export function answered(data: Pick<VaultConfigData, 'report' | 'desired' | 'reportedAt'>): boolean {
  const r = data.report;
  const d = data.desired;
  return !!r && !!d && d.version > r.version && (r.state === 'rejected' || r.state === 'rolled-back') && (data.reportedAt ?? 0) >= d.updatedAt;
}

/**
 * What the Vault runs now, with the Cloud's newer desired overrides laid over it while the Vault has not answered
 * yet (offline or still applying): the form then shows what was saved, not what is about to be replaced.
 */
export function currentView(data: Pick<VaultConfigData, 'report' | 'desired' | 'reportedAt'>): { effective: Values; overrides: Values; undelivered: boolean } | null {
  const r = data.report;
  if (!r) return null;
  const waiting = undelivered(data);
  if (!waiting) return { effective: r.effective ?? {}, overrides: r.overrides ?? {}, undelivered: false };
  const overrides = data.desired!.overrides ?? {};
  // A key that lost its override falls back to vault.json, else to nothing we know here (the default is the schema's)
  const effective: Values = { ...r.effective };
  for (const k of Object.keys(r.overrides ?? {})) if (!(k in overrides)) { if (k in (r.local ?? {})) effective[k] = r.local[k]; else delete effective[k]; }
  for (const [k, v] of Object.entries(overrides)) effective[k] = v;
  return { effective, overrides, undelivered: true };
}

/**
 * The Cloud merges a save into the stored set, and a refused set stays stored. Before the next save the stored set is
 * brought back to what the Vault actually runs, so that a value the user has since dropped does not refuse the save again.
 */
export function rebase(schema: ConfigSetting[], data: Pick<VaultConfigData, 'report' | 'desired' | 'reportedAt'>, changes: Record<string, SettingValue | null>): Record<string, SettingValue | null> {
  const r = data.report;
  const d = data.desired;
  if (!r || !d || !answered(data)) return changes;
  const secret = new Set(schema.filter((x) => x.type === 'secret' || x.sensitive).map((x) => x.key));
  const out = { ...changes };
  for (const key of new Set([...Object.keys(d.overrides ?? {}), ...Object.keys(r.overrides ?? {})])) {
    if (key in out) continue;
    const stored = d.overrides?.[key];
    const running = r.overrides?.[key];
    if (secret.has(key)) { if (stored !== undefined && running === undefined) out[key] = null; continue; }
    if (stored !== running) out[key] = running === undefined ? null : running;
  }
  return out;
}

/**
 * Where the effective value of a key comes from: an override in the Cloud, else vault.json, else the built-in default.
 * The Vault reports its whole parsed vault.json as `local` (defaults included), so vault.json counts only when its
 * value differs from the default.
 */
export function sourceOf(key: string, local: Values, overrides: Values, def?: SettingValue | null): Source {
  if (key in overrides) return 'cloud';
  if (key in local && local[key] !== (def ?? undefined) && !(def == null && (local[key] === null || local[key] === ''))) return 'vault.json';
  return 'default';
}

export type Coerced = { value: SettingValue } | { error: string };

/** The typed value of what the user entered, or why not. Secrets are checked by the caller (empty means no change). */
export function coerce(s: Pick<ConfigSetting, 'type' | 'min' | 'max' | 'options'>, raw: string | boolean): Coerced {
  switch (s.type) {
    case 'bool':
      if (typeof raw === 'boolean') return { value: raw };
      if (raw === 'true') return { value: true };
      if (raw === 'false') return { value: false };
      return { error: 'Choose on or off' };
    case 'int': {
      const text = String(raw).trim();
      if (!text) return { error: 'Enter a number' };
      if (!/^-?\d+$/.test(text)) return { error: 'Whole numbers only' };
      const n = Number(text);
      if (!Number.isSafeInteger(n)) return { error: 'That number is too large' };
      if (s.min !== undefined && s.max !== undefined && (n < s.min || n > s.max)) return { error: `Between ${s.min} and ${s.max}` };
      if (s.min !== undefined && n < s.min) return { error: `At least ${s.min}` };
      if (s.max !== undefined && n > s.max) return { error: `At most ${s.max}` };
      return { value: n };
    }
    case 'enum': {
      const v = String(raw);
      if (s.options && !s.options.includes(v)) return { error: `One of: ${s.options.join(', ')}` };
      return { value: v };
    }
    case 'path':
    case 'string':
    case 'secret':
    default:
      return { value: typeof raw === 'boolean' ? String(raw) : raw };
  }
}

/** What the user did to one setting in the form: typed a value, or asked for the vault.json value back. */
export type Edit = { value: string | boolean } | { revert: true };
export type Edits = Record<string, Edit>;

export interface Pending {
  /** What the save sends: only the keys that really change; null removes the override. */
  changes: Record<string, SettingValue | null>;
  /** Entries that cannot be sent, by key. */
  errors: Record<string, string>;
  /** How many settings the save bar counts: the changes plus the invalid ones. */
  count: number;
}

const unset = (x: unknown) => x === undefined || x === null || x === '';
const same = (a: unknown, b: unknown) => (unset(a) ? unset(b) : a === b);

/** The difference between the form and what the Vault runs: the changed keys with typed values. */
export function pendingChanges(schema: ConfigSetting[], effective: Values, overrides: Values, edits: Edits): Pending {
  const changes: Pending['changes'] = {};
  const errors: Pending['errors'] = {};
  const byKey = new Map(schema.map((s) => [s.key, s]));
  for (const [key, edit] of Object.entries(edits)) {
    const s = byKey.get(key);
    if (!s || s.readOnly) continue;
    if ('revert' in edit) {
      if (key in overrides) changes[key] = null;
      continue;
    }
    if (s.type === 'secret') {
      if (typeof edit.value === 'string' && edit.value !== '') changes[key] = edit.value;
      continue;
    }
    const c = coerce(s, edit.value);
    if ('error' in c) { errors[key] = c.error; continue; }
    if (!same(c.value, effective[key])) changes[key] = c.value;
  }
  return { changes, errors, count: Object.keys(changes).length + Object.keys(errors).length };
}

/** The label of a setting by key; the key itself when the Vault does not describe it. */
export function labelOf(schema: ConfigSetting[], key: string): string {
  return schema.find((s) => s.key === key)?.label ?? key;
}

/** "3 changes" / "1 change". */
export function changeCount(n: number): string {
  return n === 1 ? '1 change' : `${n} changes`;
}

// ---------------------------------------------------------------- the certificate source of HTTPS ingest

/** Not a Vault setting: a way of grouping the tls.* keys by where the certificate comes from. */
export type TlsSource = 'off' | 'acme' | 'pem' | 'pkcs12';
export const TLS_GROUP = 'HTTPS ingest (TLS)';
export const TLS_SOURCES: TlsSource[] = ['off', 'acme', 'pem', 'pkcs12'];

export const TLS_SOURCE_LABEL: Record<TlsSource, string> = {
  off: 'Off', acme: 'Automatic certificate (ACME)', pem: 'Own certificate files (PEM)', pkcs12: 'Own keystore (PKCS12)',
};
export const TLS_SOURCE_HELP: Record<TlsSource, string> = {
  off: 'There is no HTTPS listener: senders use the plain listener only.',
  acme: 'The Vault gets and renews its certificate itself from an ACME CA such as Let\'s Encrypt. Needs a public domain name and an e-mail address.',
  pem: 'You provide a certificate chain and an unencrypted private key as PEM files; the Vault reloads them when they change.',
  pkcs12: 'You provide a PKCS12 keystore (.p12) with its password; the Vault reloads it when it changes.',
};

/** The settings each source shows, in display order. tls.enabled and tls.acme.enabled are implied by the choice and never shown. */
export const TLS_SOURCE_KEYS: Record<TlsSource, string[]> = {
  off: [],
  acme: ['tls.port', 'tls.host', 'tls.acme.domain', 'tls.acme.email', 'tls.acme.challenge', 'tls.acme.http01Port', 'tls.acme.directory', 'tls.acme.renewDaysBefore'],
  pem: ['tls.port', 'tls.host', 'tls.certFile', 'tls.keyFile', 'tls.reloadIntervalMs'],
  pkcs12: ['tls.port', 'tls.host', 'tls.keystoreFile', 'tls.keystorePassword', 'tls.reloadIntervalMs'],
};
/** The keys the choice itself sets; never shown as rows. */
export const TLS_IMPLIED_KEYS = ['tls.enabled', 'tls.acme.enabled'];
/** Path keys of the own-certificate sources: the Vault rejects ACME together with any of them, so leaving them means emptying them. */
const TLS_FILE_KEYS = { pem: ['tls.certFile', 'tls.keyFile'], pkcs12: ['tls.keystoreFile'] } as const;

const filled = (v: unknown) => typeof v === 'string' ? v.trim() !== '' : v !== undefined && v !== null && v !== false;

/**
 * The source the Vault is configured for, from its effective values (it reports every key, so empty strings and
 * defaults are everywhere): ACME on, else a keystore, else certificate/key files, else off. `incomplete` is the
 * state "HTTPS is on but no certificate source is set": shown as off, with a hint.
 */
export function tlsSourceOf(values: Values): { source: TlsSource; incomplete: boolean } {
  if (values['tls.enabled'] === false) return { source: 'off', incomplete: false };
  if (values['tls.acme.enabled'] === true) return { source: 'acme', incomplete: false };
  if (filled(values['tls.keystoreFile'])) return { source: 'pkcs12', incomplete: false };
  if (filled(values['tls.certFile']) || filled(values['tls.keyFile'])) return { source: 'pem', incomplete: false };
  return { source: 'off', incomplete: values['tls.enabled'] === true };
}

/**
 * What choosing a source puts into the draft, as typed values (keys the Vault's schema does not have are left out):
 * the switches that belong to it, and an EMPTY STRING for the path keys of the other own-certificate source (the Vault
 * treats '' as unset, and unlike deleting an override this also masks a value that comes from vault.json). Keys that
 * are shared (port, host) and settings of the other sources that cannot clash (ACME domain, e-mail, ..., the
 * keystore password) are left alone.
 */
export function tlsSourceEdits(target: TlsSource, schemaKeys: Iterable<string>): Record<string, string | boolean> {
  const has = new Set(schemaKeys);
  const out: Record<string, string | boolean> = {};
  const put = (k: string, v: string | boolean) => { if (has.has(k)) out[k] = v; };
  put('tls.enabled', target !== 'off');
  put('tls.acme.enabled', target === 'acme');
  if (target === 'acme') for (const k of [...TLS_FILE_KEYS.pem, ...TLS_FILE_KEYS.pkcs12]) put(k, '');
  if (target === 'pem') for (const k of TLS_FILE_KEYS.pkcs12) put(k, '');
  if (target === 'pkcs12') for (const k of TLS_FILE_KEYS.pem) put(k, '');
  return out;
}

/**
 * The draft after choosing a source: what the previous source had typed and the new one does not show is dropped (a
 * hidden group never sends stray values), typed path emptyings of the new source's own keys are undone, and the
 * source's own switches and emptyings are set. Everything outside the TLS group is untouched.
 */
export function switchTlsSource(edits: Edits, target: TlsSource, schema: Pick<ConfigSetting, 'key' | 'group'>[]): Edits {
  const groupKeys = new Set(schema.filter((s) => (s.group?.trim() || 'General') === TLS_GROUP).map((s) => s.key));
  const visible = new Set(TLS_SOURCE_KEYS[target]);
  const fileKeys = new Set<string>([...TLS_FILE_KEYS.pem, ...TLS_FILE_KEYS.pkcs12]);
  const next: Edits = {};
  for (const [k, e] of Object.entries(edits)) {
    if (!groupKeys.has(k)) next[k] = e;
    else if (visible.has(k)) {
      // an emptying left by an earlier choice is undone when this source shows the key again
      const emptied = 'value' in e && e.value === '' && fileKeys.has(k);
      if (!emptied) next[k] = e;
    }
  }
  for (const [k, v] of Object.entries(tlsSourceEdits(target, schema.map((s) => s.key)))) next[k] = { value: v };
  return next;
}

// ---------------------------------------------------------------- what the top of the tab says

export type ConfigStatus =
  | { kind: 'unreported' }
  | { kind: 'queued' }               // saved in the Cloud, the Vault is offline: delivered when it reconnects
  | { kind: 'delivering' }           // saved, the Vault is online and has not answered yet
  | { kind: 'applied' | 'pending-restart' | 'rejected' | 'rolled-back' };

export function configStatus(data: Pick<VaultConfigData, 'report' | 'desired' | 'online' | 'reportedAt'>): ConfigStatus {
  const r = data.report;
  if (!r) return { kind: 'unreported' };
  if (undelivered(data)) return data.online ? { kind: 'delivering' } : { kind: 'queued' };
  return { kind: r.state };
}

/** The chip on the Overview tab (and the list): which states need a look. Applied and unknown need none. */
export function configAttention(c: Pick<ConfigSummary, 'state'> | null | undefined): { label: string; tone: 'warn' | 'fail' } | null {
  switch (c?.state) {
    case 'pending-restart': return { label: 'Restart required', tone: 'warn' };
    case 'rejected': return { label: 'Settings rejected', tone: 'fail' };
    case 'rolled-back': return { label: 'Rolled back', tone: 'fail' };
    case 'out-of-date': return { label: 'Settings not applied', tone: 'warn' };
    default: return null;
  }
}

// ---------------------------------------------------------------- polling

export const FAST_POLL_MS = 2000;
export const FAST_WINDOW_MS = 20_000;

/** 2 s for 20 s after a save (or while restarting), then the normal interval. */
export function pollInterval(fastUntil: number, now: number, normal: number): number {
  return now < fastUntil ? FAST_POLL_MS : normal;
}

// ---------------------------------------------------------------- restart progress

export const RESTART_TIMEOUT_MS = 120_000;

export type RestartState =
  | { kind: 'idle' }
  | { kind: 'waiting'; startedAt: number; prevStart: number | null }
  | { kind: 'back'; at: number | null; version: string | null }
  | { kind: 'timeout'; startedAt: number; prevStart: number | null };

export interface Observation { now: number; online: boolean; lastStartAt: number | null; version?: string | null }

export function beginRestart(now: number, lastStartAt: number | null): RestartState {
  return { kind: 'waiting', startedAt: now, prevStart: lastStartAt };
}

/** Done when the Vault is online AND reports a later start than before; 120 s without that is a timeout (which still recovers if it comes back). */
export function stepRestart(s: RestartState, o: Observation): RestartState {
  if (s.kind !== 'waiting' && s.kind !== 'timeout') return s;
  if (o.online && o.lastStartAt !== null && o.lastStartAt > (s.prevStart ?? 0)) return { kind: 'back', at: o.lastStartAt, version: o.version ?? null };
  if (s.kind === 'waiting' && o.now - s.startedAt >= RESTART_TIMEOUT_MS) return { kind: 'timeout', startedAt: s.startedAt, prevStart: s.prevStart };
  return s;
}

/** Seconds since the restart began, for the progress line. */
export function restartElapsed(s: RestartState, now: number): number | null {
  return s.kind === 'waiting' ? Math.max(0, Math.floor((now - s.startedAt) / 1000)) : null;
}

export const RESTART_COMMAND = 'systemctl restart messagevault-vault';
export const STATUS_COMMAND = 'systemctl status messagevault-vault';
export const JOURNAL_COMMAND = 'journalctl -u messagevault-vault -n 100';

// ---------------------------------------------------------------- logs

export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/** The level of a log line, from the word the Vault wrote; null when there is none. */
export function logLevel(line: string): LogLevel | null {
  const m = /\b(ERROR|SEVERE|FATAL|WARN(?:ING)?|INFO|CONFIG|DEBUG|TRACE|FINE(?:R|ST)?)\b/.exec(line.slice(0, 160));
  if (!m) return null;
  switch (m[1]) {
    case 'ERROR': case 'SEVERE': case 'FATAL': return 'error';
    case 'WARN': case 'WARNING': return 'warn';
    case 'INFO': case 'CONFIG': return 'info';
    default: return 'debug';
  }
}

/** A line cut around its level word, so that only the word is coloured: [before, word, after]; the whole line as `before` when it has none. */
export function splitLevel(line: string): [string, string, string] {
  const m = /\b(ERROR|SEVERE|FATAL|WARN(?:ING)?|INFO|CONFIG|DEBUG|TRACE|FINE(?:R|ST)?)\b/.exec(line.slice(0, 160));
  if (!m) return [line, '', ''];
  return [line.slice(0, m.index), m[1], line.slice(m.index + m[1].length)];
}

/** Lines containing the text (case-insensitive); all of them when the filter is empty. */
export function filterLines(lines: string[], text: string): string[] {
  const t = text.trim().toLowerCase();
  return t ? lines.filter((l) => l.toLowerCase().includes(t)) : lines;
}
